// Client-side MySQL schema cache, keyed by connection and scoped to one
// database.
//
// TIERED LOAD (SLICES.md § S7 items 1-4, 7-8 — the MSSQL design of
// `Docs/IntelliSenseSnapshotLoad.md` applied to mysql):
//
//   phase 1 — the relation list (`information_schema.TABLES`) plus the
//             session-variable probe (`@@sql_mode`, `@@lower_case_table_names`,
//             `VERSION()`) and the database list. Published to the schema bus
//             the moment it lands, which flips the IntelliSense pill to ready
//             and makes relation-name completion work. `ensureMysqlSchema`
//             resolves here.
//   phase 2 — columns, indexes, FK edges, the routine list and routine
//             signatures, fetched afterwards, merged page by page into the
//             SAME snapshot object and re-published as they arrive
//             (`whenMysqlSnapshotComplete` awaits it).
//
// Every phase-2 fetch is paged (`introspectionPaging.ts`, 5,000 rows per
// page), gated to 2 concurrent queries per connection
// (`introspectionSemaphore.ts`), and merged incrementally
// (`mysqlSnapshotMerge.ts`) with an event-loop yield between pages so no
// main-thread task exceeds 50 ms.

import { getSnapshot as getSchemaBusSnapshot } from '@/features/intellisense/schemaBus/schemaBus';
import { projectMysqlSnapshot } from '@/features/intellisense/schemaBus/adapters/mysqlAdapter';

import { isCompleteOutcome, INTROSPECTION_PAGE_ROWS, yieldToEventLoop } from './introspectionPaging';
import {
  fetchMysqlColumnPages,
  fetchMysqlDatabases,
  fetchMysqlForeignKeyPages,
  fetchMysqlIndexPages,
  fetchMysqlRelationPages,
  fetchMysqlRoutineListPages,
  fetchMysqlSessionVars,
  type MysqlRelationEntry,
} from './mysqlSchemaIntrospection';
import { fetchMysqlRoutineParamPages } from './mysqlRoutineIntrospection';
import type { MysqlSchemaColumn, MysqlSchemaSnapshot } from './mysqlSchemaTypes';
import {
  applyMysqlColumnPage,
  applyMysqlForeignKeyPage,
  applyMysqlIndexPage,
  applyMysqlRoutineListPage,
  applyMysqlRoutineParamPage,
  carryOverMysqlIntrospection,
  createMysqlLiveSnapshot,
  type MysqlLiveSnapshot,
  reconcileMysqlIndexes,
} from './mysqlSnapshotMerge';
import { beginIntrospectionLoad } from './introspectionTruncation';
import { SNAPSHOT_STALE_AFTER_MS } from './snapshotLifetime';
import {
  createLoadFailures,
  createTieredCacheEntry,
  isEntryFresh,
  markLoadOutcome,
  publishDegraded,
  publishThrottled,
  recordLoadFailure,
  reportPartialSnapshot,
  whenEntryColumnsLoaded,
  whenEntryComplete,
  type LoadFailures,
  type TieredCacheEntry,
} from './tieredSnapshotCache';

type CacheEntry = TieredCacheEntry<MysqlSchemaSnapshot>;

const cacheByConnectionId = new Map<string, CacheEntry>();
// S7 item 3 — the snapshot is built once per connection and rebuilt only on
// DDL invalidation, explicit `forceRefresh`, or reconnect. This is a
// day-scale backstop, NOT a refresh cadence (was 30_000, which re-ran the
// whole information_schema fan-out while the user was editing).
const DEFAULT_TTL_MS = SNAPSHOT_STALE_AFTER_MS;


function entryFor(connectionId: string): CacheEntry {
  const existing = cacheByConnectionId.get(connectionId);
  if (existing) return existing;
  const created = createTieredCacheEntry<MysqlSchemaSnapshot>();
  cacheByConnectionId.set(connectionId, created);
  return created;
}

export function getCachedMysqlSchema(connectionId: string): MysqlSchemaSnapshot | undefined {
  return cacheByConnectionId.get(connectionId)?.snapshot;
}

export function invalidateMysqlSchema(connectionId: string): void {
  const entry = cacheByConnectionId.get(connectionId);
  if (!entry) return;
  // Bump the generation so any refresh already in flight cannot publish its
  // pre-invalidation snapshot once it resolves — and so an in-flight phase 2
  // stops merging AND stops paging.
  entry.generation += 1;
  entry.refreshPromise = undefined;
  // Soft invalidation: keep serving the last snapshot but mark it stale so the
  // next ensure() bypasses the TTL fast-path and refreshes.
  if (entry.snapshot) entry.snapshot = { ...entry.snapshot, builtAt: 0 };
}

export function dropMysqlSchema(connectionId: string): void {
  // Mutate the entry in place (rather than deleting the map key) so a refresh
  // whose closure still holds this entry object observes the bumped generation
  // and skips its stale publish/cache.
  const entry = cacheByConnectionId.get(connectionId);
  if (!entry) return;
  entry.generation += 1;
  entry.snapshot = undefined;
  entry.refreshPromise = undefined;
  entry.phase2 = undefined;
  entry.columnsLoaded = undefined;
}

/**
 * Test seam — seed the native MySQL schema cache synchronously without
 * standing up an Electron bridge. Used by the dialect-native MySQL resolver
 * tests to exercise version-gated branches (CTE support, ANSI_QUOTES) that
 * read `sqlMode` / `serverVersion` from the native cache. Gated on test
 * mode (`SQL_STUDIO_TEST=1` or `NODE_ENV=test`).
 */
export function injectMysqlSchemaForTest(
  connectionId: string,
  snapshot: MysqlSchemaSnapshot,
): void {
  if (typeof process !== 'undefined') {
    const isTest = process.env?.SQL_STUDIO_TEST === '1' || process.env?.NODE_ENV === 'test';
    if (!isTest) {
      throw new Error(
        'injectMysqlSchemaForTest is only available in test mode (SQL_STUDIO_TEST=1 or NODE_ENV=test).',
      );
    }
  }
  const entry = entryFor(connectionId);
  entry.snapshot = snapshot;
  entry.lastError = undefined;
}

export function __resetMysqlSchemaCacheForTest(): void {
  cacheByConnectionId.clear();
}

/**
 * Phase 1 — the relation list plus the session-variable and database-list
 * probes. Publishes the moment it lands and kicks phase 2 without awaiting it.
 */
async function runMysqlPhase1(
  entry: CacheEntry,
  options: { connectionId: string; database: string },
): Promise<MysqlSchemaSnapshot> {
  const { connectionId, database } = options;
  const generation = entry.generation + 1;
  entry.generation = generation;
  const failures = createLoadFailures();
  // The record describes THIS load, not the previous one (R3).
  beginIntrospectionLoad(connectionId, database);

  const relations: MysqlRelationEntry[] = [];
  let relationsFailed = false;
  await fetchMysqlRelationPages(
    connectionId,
    database,
    (page) => {
      relations.push(...page);
    },
    { shouldAbort: () => entry.generation !== generation },
  ).catch((error: unknown) => {
    relationsFailed = true;
    recordLoadFailure(failures, 'relations', error);
  });

  const [databases, sessionVars] = await Promise.all([
    fetchMysqlDatabases(connectionId, database).catch((error: unknown) => {
      recordLoadFailure(failures, 'databases', error);
      return [] as string[];
    }),
    fetchMysqlSessionVars(connectionId, database).catch((error: unknown) => {
      recordLoadFailure(failures, 'serverinfo', error);
      return { sqlMode: '', lowerCaseTableNames: null, serverVersion: '' };
    }),
  ]);

  // Same-scope only: a database switch must not carry A's columns onto B's
  // same-named tables (release review 1.5.0, F4).
  const previous = entry.snapshot?.database === database ? entry.snapshot : undefined;
  // Stale-while-revalidate: keep the previous refresh's columns / indexes /
  // FK rows on relations that still exist until phase 2 replaces them.
  const objects = carryOverMysqlIntrospection(
    previous ? previous.tables.concat(previous.views) : undefined,
    relations,
  );

  const snapshot: MysqlSchemaSnapshot = {
    connectionId,
    database,
    builtAt: Date.now(),
    tables: objects.filter((o) => o.type === 'table'),
    views: objects.filter((o) => o.type === 'view'),
    routines: previous?.routines ?? [],
    databases,
    objectsByNameLower: {},
    columnsByObjectNameLower: {},
    columnIndexByNameLower: {},
    routinesByNameLower: previous?.routinesByNameLower ?? {},
    sqlMode: sessionVars.sqlMode,
    lowerCaseTableNames: sessionVars.lowerCaseTableNames,
    serverVersion: sessionVars.serverVersion,
    phase: 'tables',
  };

  if (relationsFailed) {
    return publishDegraded(entry, generation, {
      previous,
      built: snapshot,
      failures,
      engine: 'mysql',
      project: projectMysqlSnapshot,
    });
  }

  const live = await createMysqlLiveSnapshot(snapshot);
  // A drop / newer refresh landed while we were projecting — discard.
  if (entry.generation !== generation) return snapshot;

  entry.snapshot = snapshot;
  entry.lastError = undefined;
  publishThrottled(entry, live.projected, true);
  entry.phase2 = runMysqlPhase2(entry, live, generation, failures);
  return snapshot;
}

/**
 * Phase 2 — columns, indexes, FK edges, routines and routine signatures,
 * paged, merged incrementally and re-published as pages land. Never rejects:
 * an artefact that fails degrades to whatever pages already merged and is
 * named in the `feature:schema_snapshot_partial` event.
 */
async function runMysqlPhase2(
  entry: CacheEntry,
  live: MysqlLiveSnapshot,
  generation: number,
  failures: LoadFailures,
): Promise<void> {
  const { connectionId, database } = live.native;
  const superseded = (): boolean => entry.generation !== generation;

  const onPage = (merge: () => void): void => {
    if (superseded()) return;
    merge();
    publishThrottled(entry, live.projected, false);
  };

  const step = async (name: string, run: () => Promise<void>): Promise<void> => {
    if (superseded()) return;
    try {
      await run();
    } catch (error) {
      recordLoadFailure(failures, name, error);
    }
    if (!superseded()) publishThrottled(entry, live.projected, false);
  };

  const abort = { shouldAbort: superseded };
  // Exposed as `columnsLoaded` (set before this function first yields) so the
  // Visual Query Editor can start editing without waiting for the other steps.
  const columns = step('columns', () =>
    fetchMysqlColumnPages(
      connectionId,
      database,
      (page) => onPage(() => applyMysqlColumnPage(live, page)),
      abort,
    ),
  );
  entry.columnsLoaded = columns;
  await Promise.all([
    columns,
    step('indexes', async () => {
      const outcome = await fetchMysqlIndexPages(
        connectionId,
        database,
        (page) => onPage(() => applyMysqlIndexPage(live, page)),
        abort,
      );
      // Only a COMPLETE read may clear carried-over indexes on untouched
      // relations; a capped or aborted one proves nothing about them.
      if (isCompleteOutcome(outcome)) onPage(() => reconcileMysqlIndexes(live));
    }),
    step('fk', () =>
      fetchMysqlForeignKeyPages(
        connectionId,
        database,
        (page) => onPage(() => applyMysqlForeignKeyPage(live, page)),
        abort,
      ),
    ),
    step('routines', () =>
      fetchMysqlRoutineListPages(
        connectionId,
        database,
        (page) => onPage(() => applyMysqlRoutineListPage(live, page)),
        abort,
      ),
    ),
    step('routine-params', () =>
      fetchMysqlRoutineParamPages(
        connectionId,
        database,
        (page) => onPage(() => applyMysqlRoutineParamPage(live, page)),
        abort,
      ),
    ),
  ]);

  if (superseded()) return;
  live.native.phase = 'complete';
  markLoadOutcome(entry, failures.missing, { connectionId, database });
  publishThrottled(entry, live.projected, true);
  reportPartialSnapshot('mysql', failures);
}

export async function ensureMysqlSchema(options: {
  connectionId: string;
  database: string;
  forceRefresh?: boolean;
  ttlMs?: number;
}): Promise<MysqlSchemaSnapshot> {
  const ttlMs = options.ttlMs ?? DEFAULT_TTL_MS;
  const entry = entryFor(options.connectionId);

  // The cache is keyed by connection, but a snapshot describes ONE database.
  // With the day-scale TTL a database switch would otherwise keep serving the
  // previous database's objects forever, so a scope mismatch counts as a miss.
  const scopeMatches = entry.snapshot?.database === options.database;

  if (
    !options.forceRefresh
    && entry.snapshot
    && scopeMatches
    && isEntryFresh(entry, ttlMs)
  ) {
    // The bus is cleared independently of this cache — by the DDL invalidator
    // and by disconnect/reconnect (`invalidateConnection`). Re-publish here
    // whenever the bus lost the entry, or the resolver would read a cold bus
    // forever.
    if (!getSchemaBusSnapshot(options.connectionId, options.database)) {
      publishThrottled(entry, projectMysqlSnapshot(entry.snapshot), true);
    }
    return entry.snapshot;
  }

  // Dedup only a refresh of the SAME database (release review 1.5.0, F4).
  if (entry.refreshPromise && entry.refreshScope === options.database) {
    return entry.refreshPromise;
  }

  const refresh = runMysqlPhase1(entry, options)
    .catch((error: unknown) => {
      entry.lastError = error instanceof Error ? error.message : 'Failed to load MySQL schema';
      throw error;
    })
    .finally(() => {
      // Only clear OUR in-flight marker: a drop/invalidate may have handed
      // `refreshPromise` to a newer refresh while we were in flight.
      if (entry.refreshPromise === refresh) {
        entry.refreshPromise = undefined;
        entry.refreshScope = undefined;
      }
    });
  entry.refreshPromise = refresh;
  entry.refreshScope = options.database;
  return refresh;
}

/**
 * Resolve once the snapshot for `connectionId` is fully loaded — phase 1
 * published AND phase 2 finished merging. Never rejects.
 */
export async function whenMysqlSnapshotComplete(connectionId: string): Promise<void> {
  await whenEntryComplete(cacheByConnectionId.get(connectionId));
}

/**
 * Resolve once the snapshot's columns have loaded (phase 1 + the phase-2
 * column step), without waiting for indexes, FK edges, routines or routine
 * signatures — see `whenEntryColumnsLoaded`.
 */
export async function whenMysqlColumnsLoaded(connectionId: string): Promise<void> {
  await whenEntryColumnsLoaded(cacheByConnectionId.get(connectionId));
}

/** `ensureMysqlSchema` + `whenMysqlColumnsLoaded` — the Visual Query Editor's
 *  live schema pull: editable once tables + columns are in; the later
 *  artefacts arrive through the schema bus. */
export async function ensureMysqlSchemaWithColumns(options: {
  connectionId: string;
  database: string;
  forceRefresh?: boolean;
  ttlMs?: number;
}): Promise<MysqlSchemaSnapshot> {
  const snapshot = await ensureMysqlSchema(options);
  await whenMysqlColumnsLoaded(options.connectionId);
  return getCachedMysqlSchema(options.connectionId) ?? snapshot;
}

/** `ensureMysqlSchema` + `whenMysqlSnapshotComplete` — for callers that need
 *  the WHOLE snapshot (the coverage harness's post-DDL hook, the pill's
 *  "Reload schema" action). */
export async function ensureMysqlSchemaComplete(options: {
  connectionId: string;
  database: string;
  forceRefresh?: boolean;
  ttlMs?: number;
}): Promise<MysqlSchemaSnapshot> {
  const snapshot = await ensureMysqlSchema(options);
  await whenMysqlSnapshotComplete(options.connectionId);
  return getCachedMysqlSchema(options.connectionId) ?? snapshot;
}

/**
 * Test seam for the long-task perf gate: build a synthetic catalog of
 * `tableCount` relations x `columnsPerTable` columns and push it through the
 * REAL merge path — the same chunked `createMysqlLiveSnapshot` projection, the
 * same `applyMysqlColumnPage` merge in `INTROSPECTION_PAGE_ROWS`-sized pages,
 * the same throttled bus publishes.
 */
export async function injectSyntheticMysqlSnapshotForTest(options: {
  connectionId: string;
  database: string;
  tableCount: number;
  columnsPerTable: number;
}): Promise<void> {
  const entry = entryFor(options.connectionId);
  entry.generation += 1;

  const tables = [];
  for (let i = 0; i < options.tableCount; i += 1) {
    tables.push({
      name: `synthetic_table_${i}`,
      type: 'table' as const,
      columns: [] as MysqlSchemaColumn[],
      indexes: [],
      foreignKeys: [],
    });
    if (tables.length % INTROSPECTION_PAGE_ROWS === 0) await yieldToEventLoop();
  }

  const snapshot: MysqlSchemaSnapshot = {
    connectionId: options.connectionId,
    database: options.database,
    builtAt: Date.now(),
    tables,
    views: [],
    routines: [],
    databases: [options.database],
    objectsByNameLower: {},
    columnsByObjectNameLower: {},
    columnIndexByNameLower: {},
    routinesByNameLower: {},
    sqlMode: '',
    lowerCaseTableNames: null,
    serverVersion: '',
    phase: 'tables',
  };
  const live = await createMysqlLiveSnapshot(snapshot);
  entry.snapshot = snapshot;
  publishThrottled(entry, live.projected, true);

  let page: { table: string; item: MysqlSchemaColumn }[] = [];
  const flush = async (): Promise<void> => {
    if (page.length === 0) return;
    applyMysqlColumnPage(live, page);
    page = [];
    publishThrottled(entry, live.projected, false);
    await yieldToEventLoop();
  };
  for (let t = 0; t < options.tableCount; t += 1) {
    for (let c = 0; c < options.columnsPerTable; c += 1) {
      page.push({
        table: `synthetic_table_${t}`,
        item: {
          name: `col_${c}`,
          dataType: 'varchar(100)',
          isNullable: true,
          isPrimaryKey: c === 0,
          isAutoIncrement: false,
          ordinalPosition: c + 1,
        },
      });
      if (page.length >= INTROSPECTION_PAGE_ROWS) await flush();
    }
  }
  await flush();
  live.native.phase = 'complete';
  publishThrottled(entry, live.projected, true);
}
